import type { ChatMessage, ChatPart } from '@core/tellent-assistant-types';

interface MastraToolInvocationPart {
  type: 'tool-invocation';
  toolInvocation: {
    state: 'call' | 'result' | 'partial-call';
    toolCallId: string;
    toolName: string;
    args: unknown;
    result?: unknown;
  };
}

interface MastraTextPart {
  type: 'text';
  text: string;
  createdAt?: number;
}

interface MastraStepStartPart {
  type: 'step-start';
  createdAt?: number;
}

type MastraPart =
  | MastraTextPart
  | MastraToolInvocationPart
  | MastraStepStartPart
  | { type: string };

interface MastraMessageContent {
  format?: number;
  parts?: MastraPart[];
  content?: string;
}

interface MastraMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string | MastraMessageContent;
  createdAt?: string;
}

export const toChatMessages = (raw: readonly unknown[]): ChatMessage[] => {
  const out: ChatMessage[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const m = <MastraMessage>item;
    if (m.role !== 'user' && m.role !== 'assistant') continue;
    const id = typeof m.id === 'string' ? m.id : crypto.randomUUID();
    const parts = extractParts(m.content, id);
    if (parts.length === 0) continue;
    out.push({
      id,
      role: m.role,
      parts,
      createdAt: m.createdAt ? new Date(m.createdAt) : new Date(),
    });
  }
  return out;
};

function extractParts(content: MastraMessage['content'], messageId: string): ChatPart[] {
  if (typeof content === 'string') {
    return content ? [{ kind: 'text', messageId, content, done: true }] : [];
  }
  if (!content || typeof content !== 'object') return [];

  const parts: ChatPart[] = [];
  const rawParts = Array.isArray(content.parts) ? content.parts : [];

  for (const p of rawParts) {
    if (!p || typeof p !== 'object' || typeof p.type !== 'string') continue;
    if (p.type === 'text') {
      const textPart = <MastraTextPart>p;
      if (textPart.text) {
        parts.push({ kind: 'text', messageId, content: textPart.text, done: true });
      }
    } else if (p.type === 'tool-invocation') {
      const tp = <MastraToolInvocationPart>p;
      const inv = tp.toolInvocation;
      parts.push({
        kind: 'tool-call',
        toolCallId: inv.toolCallId,
        name: inv.toolName,
        args: inv.args,
        status: inv.state === 'result' ? 'done' : 'running',
        startedAt: 0,
      });
      if (inv.state === 'result') {
        parts.push({
          kind: 'tool-result',
          toolCallId: inv.toolCallId,
          name: inv.toolName,
          payload: inv.result,
        });
      }
    }
  }

  if (parts.length === 0 && typeof content.content === 'string' && content.content) {
    parts.push({ kind: 'text', messageId, content: content.content, done: true });
  }

  return parts;
}
